import { Box, Chip, Typography } from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";
import { IncidentDetail, IncidentVolume } from "../../types/incidentDetail";
import LabelValueGrid from "../Detail/LabelValueGrid";
import { codeWords } from "../Detail/format";

const Dated: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

// A caption beneath a value, in the quieter colour
const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" component="div" color="text.secondary">
    {children}
  </Typography>
);

// The volume as the incident states it: the document's wording where it
// has one, else the amount or the range with its qualifier and currency
export const volumeText = (volume: IncidentVolume): string => {
  if (volume.text) {
    return volume.text;
  }
  const amount =
    volume.value !== null
      ? String(volume.value)
      : volume.minimum !== null && volume.maximum !== null
        ? `${volume.minimum}–${volume.maximum}`
        : volume.minimum !== null
          ? `≥ ${volume.minimum}`
          : volume.maximum !== null
            ? `≤ ${volume.maximum}`
            : "";
  return [volume.qualifier, amount, volume.currency]
    .filter(Boolean)
    .join(" ");
};

// The dates as stated: "2026-08-11 → 2026-08-12", or one date alone
const datesText = (incident: IncidentDetail): React.ReactNode => {
  if (incident.start && incident.end) {
    return incident.start === incident.end ? (
      <Dated>{incident.start}</Dated>
    ) : (
      <>
        <Dated>{incident.start}</Dated>
        {" → "}
        <Dated>{incident.end}</Dated>
      </>
    );
  }
  if (incident.start) {
    return (
      <>
        {"from "}
        <Dated>{incident.start}</Dated>
      </>
    );
  }
  if (incident.end) {
    return (
      <>
        {"until "}
        <Dated>{incident.end}</Dated>
      </>
    );
  }
  return null;
};

// Whether the incident has anything the overview shows
export const hasIncidentOverview = (incident: IncidentDetail): boolean =>
  Boolean(
    incident.description ||
      incident.start ||
      incident.end ||
      incident.status.length > 0 ||
      incident.status_summary ||
      (incident.volume && volumeText(incident.volume)) ||
      incident.keywords.length > 0,
  );

/**
 * The overview of an incident: its dates as stated, its status on one line
 * with the wording beneath, its volume, its keywords as chips and its
 * description.
 */
const IncidentOverview: React.FC<{ incident: IncidentDetail }> = ({
  incident,
}) => {
  const dates = datesText(incident);
  const status = incident.status.map(codeWords).join(", ");
  const volume = incident.volume ? volumeText(incident.volume) : "";

  return (
    <LabelValueGrid
      rows={[
        {
          label: "Dates",
          value: dates && (
            <>
              {dates}
              <Caption>as stated</Caption>
            </>
          ),
        },
        {
          label: "Status",
          value: (status || incident.status_summary) && (
            <>
              {status}
              {incident.status_summary && (
                <Caption>“{incident.status_summary}”</Caption>
              )}
            </>
          ),
        },
        { label: "Volume", value: volume && <Dated>{volume}</Dated> },
        {
          label: "Keywords",
          value: incident.keywords.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {incident.keywords.map((keyword) => (
                <Chip
                  key={keyword}
                  label={keyword}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.75rem" }}
                />
              ))}
            </Box>
          ),
        },
        { label: "Description", value: incident.description, wide: true },
      ]}
    />
  );
};

export default IncidentOverview;
